import React from "react";
import styled, { keyframes } from "styled-components";

// Animations
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const rotate = keyframes`
  100% { transform: rotate(360deg); }
`;

const dash = keyframes`
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
`;

const progress = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

// Container with blue to purple gradient
const LoaderContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #3b5998 0%, #5b7cc7 25%, #7b4ba4 75%, #8b3a9e 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: ${fadeIn} 0.3s ease-in;
`;

const LoaderWrapper = styled.div`
  text-align: center;
`;

// Gradient Spinner
const GradientSpinner = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(45deg, #fff, transparent);
  animation: ${spin} 1s linear infinite;
  position: relative;
  margin: 0 auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    bottom: 10px;
    background: linear-gradient(135deg, #3b5998 0%, #8b3a9e 100%);
    border-radius: 50%;
  }
`;

// Dots
const DotsContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 30px;
`;

const Dot = styled.div`
  width: 20px;
  height: 20px;
  background: #ffffff;
  border-radius: 50%;
  animation: ${bounce} 1.4s infinite ease-in-out;
  
  &:nth-child(1) { animation-delay: -0.32s; }
  &:nth-child(2) { animation-delay: -0.16s; }
  &:nth-child(3) { animation-delay: 0s; }
`;

// Ring Spinner
const RingSpinner = styled.div`
  width: 80px;
  height: 80px;
  border: 6px solid transparent;
  border-top: 6px solid #ffffff;
  border-right: 6px solid #ffffff;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto;
  box-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    border: 4px solid transparent;
    border-bottom: 4px solid rgba(255, 255, 255, 0.6);
    border-left: 4px solid rgba(255, 255, 255, 0.6);
    border-radius: 50%;
    animation: ${spin} 0.75s linear infinite reverse;
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  }
`;

// Minimal SVG
const SpinnerSvg = styled.svg`
  animation: ${rotate} 2s linear infinite;
  width: 80px;
  height: 80px;
  margin: 0 auto;
`;

const SpinnerCircle = styled.circle`
  stroke: #ffffff;
  stroke-linecap: round;
  animation: ${dash} 1.5s ease-in-out infinite;
  fill: none;
  stroke-width: 4;
`;

// Progress Bar
const ProgressBar = styled.div`
  width: 300px;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #ffffff;
  border-radius: 10px;
  animation: ${progress} 2s ease-in-out infinite;
  box-shadow: 0 2px 10px rgba(255, 255, 255, 0.5);
`;

// Text
const LoaderText = styled.p`
  margin-top: ${props => props.type === 'dots' ? '0px' : '30px'};
  font-size: 18px;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: 2px;
`;

const Loader = ({ type = 'gradient' }) => {
  const renderLoader = () => {
    switch (type) {
      case 'gradient':
        return <GradientSpinner />;
      case 'dots':
        return (
          <DotsContainer>
            <Dot />
            <Dot />
            <Dot />
          </DotsContainer>
        );
      case 'ring':
        return <RingSpinner />;
      case 'minimal':
        return (
          <SpinnerSvg viewBox="0 0 50 50">
            <SpinnerCircle cx="25" cy="25" r="20" />
          </SpinnerSvg>
        );
      case 'progress':
        return (
          <ProgressBar>
            <ProgressFill />
          </ProgressBar>
        );
      default:
        return <GradientSpinner />;
    }
  };

  return (
    <LoaderContainer>
      <LoaderWrapper>
        {renderLoader()}
        <LoaderText type={type}>Loading...</LoaderText>
      </LoaderWrapper>
    </LoaderContainer>
  );
};

export default Loader;
